import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import { css } from 'styled-components';

type VariantStyles = {
  backgroundColor: string;
  borderColor: string;
  color: string;
  hoverBorderColor: string;
};

const createVariantStyles = ({
  backgroundColor,
  borderColor,
  color,
  hoverBorderColor,
}: VariantStyles) => css`
  background-color: ${backgroundColor};
  color: ${color};
  border: 1px solid ${borderColor};

  &:visited {
    background-color: ${backgroundColor};
    color: ${color};
    border-color: ${borderColor};
  }

  /* Hover darkens the outline and leaves the fill alone. This is deliberately
     unlike Button, which tints its fill with tint.hover: the icon button is
     always an outlined shape, so the outline is what carries the state. */
  &:hover:not([disabled]):not([aria-disabled='true']) {
    border-color: ${hoverBorderColor};
  }

  &[disabled],
  &[aria-disabled='true'] {
    background-color: var(${themeVariables.colors.surface.disabled});
    border-color: var(${themeVariables.colors.surface.disabled});
    color: var(${themeVariables.colors.content.disabled});
  }
`;

export const root = css`
  align-items: center;
  appearance: none;
  border-radius: var(${themeVariables.borderRadius.full.mobile});
  box-shadow: 0 0 4px 0
    color-mix(in srgb, var(${themeVariables.colors.content.primary}) 10%, transparent);
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  line-height: 0;
  padding: var(${themeVariables.spacing.sm});
  text-decoration: none;

  &[disabled],
  &[aria-disabled='true'] {
    cursor: not-allowed;
    /* A disabled control is not raised off the page, so it loses the elevation. */
    box-shadow: none;
  }
`;

// The IconButton has a single appearance: an outlined pill on a solid surface.
export const appearance = css`
  ${createVariantStyles({
    backgroundColor: `var(${themeVariables.colors.surface.default})`,
    borderColor: `var(${themeVariables.colors.stroke.default})`,
    color: `var(${themeVariables.colors.content.interactive})`,
    hoverBorderColor: `var(${themeVariables.colors.stroke.interactive})`,
  })}
`;

export const compact = css`
  padding: var(${themeVariables.spacing.xs});
`;

export const icon = css`
  align-items: center;
  display: inline-flex;
  line-height: 0;

  & > svg {
    display: block;
  }
`;
